import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertCircle,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Gift,
    ImageIcon,
    RefreshCw,
    Search,
} from 'lucide-react';

import { API_BASE_URL } from '../../config';

const PROVIDERS = [
    { id: '', label: 'Все' },
    { id: 'telegram', label: 'Telegram' },
    { id: 'fragment', label: 'Fragment' },
];

const SOURCE_LABELS = {
    telegram: 'Telegram',
    fragment: 'Fragment',
};

const KIND_LABELS = {
    standard_gift: 'Обычный подарок',
    collectible_model: 'Модель',
    collection: 'Коллекция',
};

const STATUS_LABELS = {
    success: 'Обновлено',
    partial: 'Частично',
    failed: 'Ошибка',
    running: 'Обновляется',
};

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const dateTime = (value) => {
    if (!value) return 'Ещё не обновлялось';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Нет данных';
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const number = (value) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toLocaleString('ru-RU');
};

const price = (item) => {
    if (item.price === null || item.price === undefined) return '—';
    const value = Number(item.price);
    if (!Number.isFinite(value)) return '—';
    return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 9 })} ${item.currency || ''}`.trim();
};

const SourceState = ({ source }) => {
    const run = source.latest_run;
    const status = run?.status || 'idle';
    const failed = status === 'failed';
    const partial = status === 'partial';
    const running = status === 'running';
    const success = status === 'success';
    const statusColor = failed
        ? 'text-rose-700'
        : partial
            ? 'text-amber-700'
            : running
                ? 'text-indigo-700'
                : success
                    ? 'text-emerald-700'
                    : 'text-slate-500';
    return (
        <div className="min-w-0 px-4 py-3 sm:px-5">
            <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-900">{SOURCE_LABELS[source.provider] || source.provider}</span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${statusColor}`}>
                    {failed || partial
                        ? <AlertCircle size={14} />
                        : running
                            ? <RefreshCw size={14} className="animate-spin" />
                            : success
                                ? <CheckCircle2 size={14} />
                                : null}
                    {STATUS_LABELS[status] || 'Нет запуска'}
                </span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
                <span>{number(source.active_count)} позиций</span>
                <span className="truncate">{dateTime(run?.completed_at || run?.started_at)}</span>
            </div>
            {run?.error_message && (
                <div className={`mt-2 truncate text-xs ${failed ? 'text-rose-600' : 'text-amber-700'}`} title={run.error_message}>
                    {run.error_message}
                </div>
            )}
        </div>
    );
};

const GiftImage = ({ item }) => {
    const [failed, setFailed] = useState(false);
    if (!item.image_url || failed) {
        return <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-400"><ImageIcon size={18} /></div>;
    }
    return (
        <img
            src={item.image_url}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-10 w-10 shrink-0 rounded-lg border border-slate-100 bg-slate-50 object-cover"
        />
    );
};

const GiftCatalogPanel = ({ onShowToast }) => {
    const [items, setItems] = useState([]);
    const [sources, setSources] = useState([]);
    const [provider, setProvider] = useState('');
    const [draftQuery, setDraftQuery] = useState('');
    const [query, setQuery] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const pageSize = 50;

    const loadStatus = useCallback(async () => {
        const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-catalog/status`, {
            credentials: 'include',
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить состояние источников');
        const nextSources = Array.isArray(body.sources) ? body.sources : [];
        setSources(nextSources);
        return nextSources;
    }, []);

    const loadItems = useCallback(async () => {
        const params = new URLSearchParams({
            active_only: String(activeOnly),
            page: String(page),
            page_size: String(pageSize),
        });
        if (provider) params.set('provider', provider);
        if (query) params.set('query', query);
        const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-catalog?${params.toString()}`, {
            credentials: 'include',
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить каталог');
        setItems(Array.isArray(body.items) ? body.items : []);
        setTotal(Number(body.total || 0));
    }, [activeOnly, page, provider, query]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            await Promise.all([loadStatus(), loadItems()]);
        } catch (loadError) {
            setError(loadError.message || 'Не удалось загрузить каталог');
        } finally {
            setLoading(false);
        }
    }, [loadItems, loadStatus]);

    useEffect(() => {
        load();
    }, [load]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

    const selectProvider = (value) => {
        setProvider(value);
        setPage(1);
    };

    const submitSearch = (event) => {
        event.preventDefault();
        setQuery(draftQuery.trim());
        setPage(1);
    };

    const sync = async () => {
        try {
            setSyncing(true);
            setError('');
            const selectedProviders = provider
                ? [provider]
                : PROVIDERS.map((item) => item.id).filter(Boolean);
            const previousRuns = Object.fromEntries(
                sources.map((source) => [source.provider, {
                    id: Number(source.latest_run?.id || 0),
                    status: source.latest_run?.status || 'idle',
                }]),
            );
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-catalog/sync`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ providers: provider ? [provider] : null }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось запустить обновление');
            onShowToast?.('Обновление каталога поставлено в очередь');

            for (let attempt = 0; attempt < 45; attempt += 1) {
                await wait(attempt === 0 ? 1000 : 2000);
                const nextSources = await loadStatus();
                const selectedStates = nextSources.filter((source) => selectedProviders.includes(source.provider));
                const finished = selectedStates.length === selectedProviders.length
                    && selectedStates.every((source) => {
                        const run = source.latest_run;
                        const previous = previousRuns[source.provider] || { id: 0, status: 'idle' };
                        const newRunFinished = Number(run?.id || 0) > previous.id
                            && run?.status !== 'running';
                        const existingRunFinished = previous.status === 'running'
                            && Number(run?.id || 0) === previous.id
                            && run?.status !== 'running';
                        return newRunFinished || existingRunFinished;
                    });
                if (!finished) continue;

                await loadItems();
                const hasErrors = selectedStates.some((source) => source.latest_run?.status === 'failed');
                const hasWarnings = selectedStates.some((source) => source.latest_run?.status === 'partial');
                onShowToast?.(
                    hasErrors
                        ? 'Каталог обновлён частично: один из источников недоступен'
                        : hasWarnings
                            ? 'Каталог обновлён с предупреждениями'
                            : 'Каталог подарков обновлён',
                );
                return;
            }

            throw new Error('Обновление продолжается в фоне. Проверьте статус через минуту.');
        } catch (syncError) {
            setError(syncError.message || 'Не удалось запустить обновление');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="space-y-5">
            {error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
            )}

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-700"><Gift size={20} /></div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Каталог подарков</h3>
                            <p className="mt-1 text-sm text-slate-500">Telegram и Fragment</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={sync}
                        disabled={syncing}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                        {provider ? `Обновить ${SOURCE_LABELS[provider]}` : 'Обновить все'}
                    </button>
                </div>
                <div className="grid divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                    {sources.map((source) => <SourceState key={source.provider} source={source} />)}
                    {!sources.length && <div className="px-5 py-4 text-sm text-slate-500 sm:col-span-3">История обновлений пока пуста</div>}
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="space-y-4 border-b border-slate-100 p-4 sm:p-5">
                    <div className="flex gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1" aria-label="Источник каталога">
                        {PROVIDERS.map((item) => (
                            <button
                                key={item.id || 'all'}
                                type="button"
                                onClick={() => selectProvider(item.id)}
                                className={`h-9 min-w-max flex-1 rounded-md px-4 text-sm font-semibold transition ${provider === item.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <form onSubmit={submitSearch} className="flex min-w-0 flex-1 gap-2">
                            <label className="relative min-w-0 flex-1">
                                <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="search"
                                    value={draftQuery}
                                    onChange={(event) => setDraftQuery(event.target.value)}
                                    placeholder="Название, модель или ID"
                                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                />
                            </label>
                            <button type="submit" className="h-10 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800">Найти</button>
                        </form>
                        <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600">
                            <input
                                type="checkbox"
                                checked={activeOnly}
                                onChange={(event) => { setActiveOnly(event.target.checked); setPage(1); }}
                                className="h-4 w-4 accent-indigo-600"
                            />
                            Только доступные
                        </label>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                        <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                            <tr>
                                <th className="px-5 py-3">Подарок</th>
                                <th className="px-4 py-3">Источник</th>
                                <th className="px-4 py-3">Тип</th>
                                <th className="px-4 py-3 text-right">Цена</th>
                                <th className="px-4 py-3 text-right">Количество</th>
                                <th className="px-4 py-3">Изменён</th>
                                <th className="w-14 px-4 py-3"><span className="sr-only">Открыть</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((item) => (
                                <tr key={item.id} className="align-middle hover:bg-slate-50/70">
                                    <td className="px-5 py-3">
                                        <div className="flex min-w-[240px] items-center gap-3">
                                            <GiftImage item={item} />
                                            <div className="min-w-0">
                                                <div className="truncate font-semibold text-slate-900" title={item.title}>{item.title}</div>
                                                <div className="mt-1 truncate font-mono text-xs text-slate-400" title={item.external_id}>{item.external_id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-slate-700">{SOURCE_LABELS[item.provider] || item.provider}</td>
                                    <td className="px-4 py-3 text-slate-600">{KIND_LABELS[item.item_kind] || item.item_kind}</td>
                                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">{price(item)}</td>
                                    <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{number(item.available_count ?? item.total_count)}</td>
                                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{dateTime(item.last_changed_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        {item.external_url && (
                                            <a
                                                href={item.external_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Открыть у источника"
                                                className="inline-grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-indigo-600"
                                            >
                                                <ExternalLink size={17} />
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!items.length && !loading && (
                                <tr><td colSpan="7" className="px-5 py-12 text-center text-sm text-slate-500">Позиции не найдены</td></tr>
                            )}
                            {loading && (
                                <tr><td colSpan="7" className="px-5 py-12 text-center text-sm text-slate-500">Загрузка каталога...</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-4">
                    <span className="text-xs text-slate-500">{number(total)} позиций</span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            title="Предыдущая страница"
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            disabled={page <= 1}
                            className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40"
                        ><ChevronLeft size={17} /></button>
                        <span className="min-w-20 text-center text-xs font-semibold text-slate-600">{page} из {totalPages}</span>
                        <button
                            type="button"
                            title="Следующая страница"
                            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                            disabled={page >= totalPages}
                            className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40"
                        ><ChevronRight size={17} /></button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default GiftCatalogPanel;
